
import React from 'react';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import TaskCardSkeleton from './TaskCardSkeleton';

interface TaskSectionSkeletonProps {
  title: string;
  icon: React.ReactNode;
  variant: 'amber' | 'green';
  cardCount?: number;
  showExpanded?: boolean;
  index?: number;
}

const TaskSectionSkeleton: React.FC<TaskSectionSkeletonProps> = ({
  title,
  icon,
  variant,
  cardCount = 3,
  showExpanded = true,
  index = 0
}) => {
  const gradientClasses = {
    amber: 'from-amber-50/60 to-white/40 dark:from-amber-950/20 dark:to-black/40 border-amber-200/50 dark:border-amber-900/30',
    green: 'from-green-50/60 to-white/40 dark:from-green-950/20 dark:to-black/40 border-green-200/50 dark:border-green-900/30'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className={`p-6 rounded-3xl shadow-[0_4px_20px_-1px_rgba(0,0,0,0.05)] 
        bg-gradient-to-br ${gradientClasses[variant]}
        backdrop-blur-sm border hover:shadow-lg transition-all duration-200`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-medium text-base">{title}</h3>
          <Skeleton className="h-6 w-6 rounded-full ml-2" />
        </div>
        {variant === 'green' && (
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-5 w-5 rounded" />
          </div>
        )}
      </div>

      {/* Content */}
      {showExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ delay: (index * 0.1) + 0.2, duration: 0.3 }}
          className="space-y-2"
        >
          {cardCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-6">
              <Skeleton className="h-12 w-12 rounded-full mb-3" />
              <Skeleton className="h-4 w-32 mb-1" />
              <Skeleton className="h-8 w-48 rounded-lg" />
            </div>
          ) : (
            Array.from({ length: cardCount }).map((_, i) => (
              <TaskCardSkeleton key={`task-skeleton-${i}`} index={i} />
            ))
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default TaskSectionSkeleton;
